import { createContext, MutableRefObject } from 'react';
import { captureException } from '@sentry/browser';
import { AnalyticsAccessContext } from 'js/analytics_v2/options';
import { analyticsPage } from 'js/analytics_v2/routes';
import { loadRecordHit } from 'js/utils/loadRecordHit';
import { trackClick } from 'js/utils/trackClick';
import { observeTrackingDelivery } from 'js/utils/observeTrackingDelivery';
import {
  startActivityTracking,
  stopActivityTracking,
} from 'js/core_webpack/metrics/page_activity_tracking';

export type AnalyticsTracking = {
  test: string;
  test_bucket: string;
  entry_hit_id: number | null;
  entry_source: string;
  requested_page: string;
  displayed_page: string;
  email_method: string | null;
  from_navbar: boolean;
  trigger: string | null;
};

export const AnalyticsTrackingContext = createContext<{
  tracking?: AnalyticsTracking;
  routeTracking?: MutableRefObject<AnalyticsRouteTracking | undefined>;
}>({});

export const ordinaryAnalytics = (context: AnalyticsAccessContext): boolean =>
  !context.admin_preview &&
  !context.fixtures &&
  context.viewer_id === context.viewed_user_id;

export const analyticsClick = (
  tracking: AnalyticsTracking | undefined,
  context: AnalyticsAccessContext,
  source: string,
  extra: Record<string, unknown>,
): void => {
  if (!tracking || !ordinaryAnalytics(context)) return;
  try {
    trackClick({
      source,
      bucket: tracking.test_bucket,
      extra: {
        entry_hit_id: tracking.entry_hit_id,
        entry_source: tracking.entry_source,
        access_mode: context.access_mode,
        ...extra,
      },
    });
  } catch (error) {
    captureException(error);
  }
};

export class AnalyticsRouteTracking {
  private url = window.location.href;
  private generation = 0;
  private pending?: Promise<void>;
  private retryHit?: () => Promise<void>;
  private ready = true;
  private disposed = false;

  constructor(private readonly basePath: string) {}

  private samePage(left: string, right: string): boolean {
    const leftPath = new URL(left).pathname;
    const rightPath = new URL(right).pathname;
    const page = analyticsPage(leftPath, this.basePath);
    return (
      leftPath === rightPath ||
      (!!page && page === analyticsPage(rightPath, this.basePath))
    );
  }

  route(url: string): Promise<void> | undefined {
    if (this.samePage(url, this.url)) {
      this.url = url;
      return;
    }
    const referrer = this.url;
    this.url = url;
    const generation = ++this.generation;
    const previousHitId = window.Aedu?.hit_data?.id;
    this.ready = false;
    stopActivityTracking();
    const record = (): Promise<void> =>
      observeTrackingDelivery('record_hit', () =>
        loadRecordHit(true, referrer),
      ).then(() => {
        if (this.disposed || this.generation !== generation) return;
        this.pending = undefined;
        const hit = window.Aedu?.hit_data;
        this.ready =
          !!hit && hit.id !== previousHitId && this.samePage(hit.url, url);
        if (this.ready) startActivityTracking();
      });
    this.retryHit = record;
    this.pending = record();
    return this.pending;
  }

  click(send: () => void): void {
    if (this.disposed) return;
    if (this.ready) {
      send();
      return;
    }
    if (!this.pending && this.retryHit) {
      const retry = this.retryHit;
      this.retryHit = undefined;
      this.pending = retry();
    }
    const generation = this.generation;
    void this.pending?.then(() => {
      if (!this.disposed && this.ready && this.generation === generation)
        send();
    });
  }

  dispose(): void {
    this.disposed = true;
  }
}

export const analyticsClickDetails = (
  target: Element,
  basePath: string,
  context: AnalyticsAccessContext,
): Record<string, unknown> | undefined => {
  const control = target.closest('a,button,[role="button"]');
  if (!control) return;
  if (
    control.closest('[role="dialog"]') ||
    document.querySelector('[data-walkthrough-scrim]')
  )
    return;
  const page = analyticsPage(window.location.pathname, basePath) ?? 'overview';
  const action = control.getAttribute('data-analytics-action');
  if (action === 'select_period' || action === 'select_category') {
    const value = control.getAttribute('data-analytics-value');
    if (!value) return;
    return {
      page,
      filter: action === 'select_period' ? 'period' : 'category',
      value,
    };
  }
  const url =
    control instanceof HTMLAnchorElement
      ? new URL(control.href, window.location.href)
      : undefined;
  const destination =
    url?.origin === window.location.origin
      ? url.pathname === '/upgrade' &&
        url.searchParams.get('feature') === 'analytics_dashboard'
        ? 'landing'
        : analyticsPage(url.pathname, basePath)
      : undefined;
  return {
    page,
    action: action ?? (url ? 'link' : 'button'),
    region: control.closest('nav') ? 'navigation' : 'content',
    destination_page: destination,
    upgrade_intent:
      context.access_mode === 'non_premium' &&
      !!destination &&
      destination !== 'overview',
  };
};
